export interface LeaveRequestForDuration {
  start_date: string;
  end_date: string;
  requested_hours?: number | null;
  requested_days?: number | null;
}

/**
 * Formats leave duration for display, handling both full-day and part-day requests
 */
export function formatLeaveDuration(request: LeaveRequestForDuration): string {
  // Check if it's a part-day request with hours specified
  if (request.requested_hours && request.requested_hours > 0) {
    return `${request.requested_hours}h`;
  }
  
  // Use requested_days if available (from database)
  if (request.requested_days && request.requested_days > 0) {
    const days = request.requested_days;
    return `${days} day${days !== 1 ? 's' : ''}`;
  }
  
  // Full-day calculation from dates as fallback
  const start = new Date(request.start_date);
  const end = new Date(request.end_date);
  const diffTime = Math.abs(end.getTime() - start.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
  return `${diffDays} day${diffDays !== 1 ? 's' : ''}`;
}

/**
 * Checks if a request is a part-day (hours-based) request
 */
export function isPartDayRequest(request: LeaveRequestForDuration): boolean {
  return !!(request.requested_hours && request.requested_hours > 0);
}

/**
 * Calculate days from date range (fallback calculation)
 */
export function calculateDaysFromDates(startDate: string, endDate: string): number {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffTime = Math.abs(end.getTime() - start.getTime());
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
}

/**
 * Get the actual requested days, using database value or calculating from dates
 */
export function getRequestedDays(request: LeaveRequestForDuration): number {
  if (request.requested_days && request.requested_days > 0) {
    return request.requested_days;
  }
  if (request.requested_hours && request.requested_hours > 0) {
    return request.requested_hours / 8; // Assume 8-hour workday
  }
  return calculateDaysFromDates(request.start_date, request.end_date);
}
